import baseAxios from 'axios';
import { axios } from '@/axios';
import searchRouter from '@/vue-router/search-router';
import EventBus from '@/event-bus';

export default {
  /**
   * Retrieves results from the Laravel backend search endpoint
   * @function
   * @param {object} store.
   * @return {Promise} - axios promise for ajax request
   */
  getAjaxResults({ state, commit, getters, dispatch }) { // eslint-disable-line
    const url = state.searchUrl;
    // Cancel pending ajax requests
    state.ajaxCancellationTokens.forEach((cancellationToken) => {
      cancellationToken('cancelled-request');
    });

    const { CancelToken } = baseAxios;
    const source = CancelToken.source();

    commit('addCancellationToken', { cancellationToken: source.cancel });

    return new Promise((resolve, reject) => {
      axios.get(url, { cancelToken: source.token })
        .then((response) => {
          const parsedUrl = new URL(`${window.location.protocol}//${window.location.host}${url}`);
          searchRouter.push({ path: parsedUrl.pathname, query: Object.fromEntries(parsedUrl.searchParams) }).catch((error) => console.log('Error related to router.push', error));
          return dispatch('setResultData', { responseData: response.data });
        })
        .then(() => {
          commit('resetCancellationTokens');
          if (document.querySelector('.SearchResults').getBoundingClientRect().top < 0 && !state.triggeredByBrowser) {
            setTimeout(() => {
              window.scrollTo({ top: 120 });
            }, 200);
          }

          commit('setLoadingState', { loadingState: false });
          commit('setTriggeredByBrowser', { toggle: false });

          window.gtag.send({
            event: document.title,
            action: 'page_view',
            category: `${window.location.pathname}${window.location.search}`,
            location: window.location.href,
          });

          resolve(true);
        }).finally(() => {
          EventBus.$emit('bus:enable-all-filters-after-search-request-is-processed');
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  /**
   * Sets the initial results that are passed in from the window via Laravel
   * @function
   * @param {object} store.
   * @return {undefined}
   */
  setInitialResults({ state, commit, dispatch, getters }) { // eslint-disable-line
    if (state.isFirstLoading && window.__RMP_SEARCH_RESULTS_INITIAL_STATE__) {
      const {
        meta: {
          query_string: {
            array: queryParams,
            string: queryString,
          },
          scopes_urls: {
            base_search_url: baseUrl,
          },
          default_sort: defaultSorts,
          ignore_aggregations: ignoreAggregations,
        },
      } = window.__RMP_SEARCH_RESULTS_INITIAL_STATE__;

      commit('setBaseUrl', { baseUrl });
      commit('setDefaultSortOrders', { defaultSorts });
      commit('setIgnoreAggregations', { ignoreAggregations });

      const defaultSort = getters.getDefaultSortOrder({ searchType: queryParams.show });

      commit('setInitialQueryState', { queryParams, defaultSort });

      dispatch('setResultData', {responseData: window.__RMP_SEARCH_RESULTS_INITIAL_STATE__}) // eslint-disable-line
        .then(() => {
          commit('setFirstLoading', { first: false });
        });

      // The API URL is set to the initial search URL constructed from the query string passed from the backend.
      const parsedUrl = new URL(queryString);
      commit('updateSearchUrl', parsedUrl.pathname + parsedUrl.search);

      // Update router
      searchRouter.push({ path: parsedUrl.pathname, query: Object.fromEntries(parsedUrl.searchParams) }).catch((error) => console.log('Error related to router.push', error));
    }
  },

  /**
   * Sets the result data to the store.state from ajax requests or initial window state
   * @function
   * @param {object} store.
   * @param {object}
   * @return {Promise}
   */
  setResultData({ state, commit }, { responseData }) {
    return new Promise((resolve) => {
      const {
        data: results,
        meta: {
          aggregations,
          services,
          total_results: count,
          pagination,
          query_string: {
            array: queryParams,
            string: queryString,
          },
          scopes_urls: defaultSearchUrls,
          sorting_urls: sortingUrls,
          human_readable_filters: dynamicHeaders,
          locations_meta: locationsMeta,
        },
      } = responseData;

      /**
       * By default, there is a watcher for state.query.
       *
       * When query is changed the watcher will trigger an ajax request, submitting the search request.
       * Think about the user checking in filters, which updates state.query which submits the search.
       *
       * All good right? Well, almost!
       *
       * There are some scenarios where we need to reset or partially update state.query but we do not want to submit the search request.
       * This means we need to disable the watcher temporarily.
       * Think about the user clicking the browser back button.
       *  - The search request is submitted and a new query value is returned by the backend
       *  - The app needs to update state.query but with the watcher disabled otherwise there would be a 2nd ajax request
       *
       *  But why is this now a thing and before it wasn't? That's a great question!
       *  When the search url was only powered by query params the state.query would "rule" the UI and the url that is submitted
       *  Now there are 2 possible search url formats, a simplified one (Google friendly) and your query params one.
       *  This meant that, now, state.query only "rules" the UI and not the url that is submitted.
       *
       * If you need to know more about this chat with Bruno.
       */
      if (state.pauseQueryWatcher) {
        commit('resetQueryParams', {
          query: queryParams,
        });
      }

      // The API URL is set to the initial search URL constructed from the query string passed from the backend.
      const parsedUrl = new URL(queryString);
      searchRouter.push({ path: parsedUrl.pathname, query: Object.fromEntries(parsedUrl.searchParams) }).catch((error) => console.log('Error related to router.push', error));

      // window.location.assign(parsedUrl.href);
      commit('updateSearchUrl', parsedUrl.pathname + parsedUrl.search);
      // commit('setSearchUrl', { url: meta.query_string.string })
      // commit('updateSearchUrl', queryString);
      commit('setSearchCount', { count });
      commit('setSearchPagination', { pagination });
      commit('setSearchLocationsMeta', { locationsMeta });
      commit('setSearchFilters', { aggregations });
      commit('setSearchResults', { results });
      commit('setSearchServices', { services });
      commit('setDynamicHeaders', { dynamicHeaders });
      commit('setDefaultUrls', defaultSearchUrls);
      commit('setSortingUrls', sortingUrls);

      state.services.featuredResults.forEach((service) => {
        // GTags - Client Portal Data
        window.gtag.send({
          event: 'cp_services',
          action: 'impression',
          id: service.service_type_id,
          category: service.service_type_name,
          category_id: service.service_type_category_id,
        });
      });

      resolve(true);
    });
  },
};
